import React from "react";

// Angela 2026-05-17 — VISUAL FINAL SCHEDULING.
// Each palette entry: bg (soft accent fill), border (subtle outline),
// activeBorder (saturated on activeQueue), counter (the colored number),
// icon (matches counter family), label (the small uppercase title).
// Labels are unified to #64748B per design spec — counter carries the color.
const colorMap = {
  // Schedule / National / SMS-RC — green family
  emerald: { bg: "bg-[#E8F5EE]", border: "border-[#1E6B4533]", activeBorder: "border-[#1E6B45]", counter: "text-[#1E6B45]", icon: "text-[#1E6B45]" },
  // Safeguard Internal / SG Tomorrow / Voicemail — amber/gold family
  amber:   { bg: "bg-[#FBF4E0]", border: "border-[#B8972E33]", activeBorder: "border-[#B8972E]", counter: "text-[#B8972E]", icon: "text-[#B8972E]" },
  // SG Today / Priority / Morning Calls / Field Updates — red family
  rose:    { bg: "bg-[#FCECEA]", border: "border-[#B5302A33]", activeBorder: "border-[#B5302A]", counter: "text-[#B5302A]", icon: "text-[#B5302A]" },
  red:     { bg: "bg-[#FCECEA]", border: "border-[#B5302A33]", activeBorder: "border-[#B5302A]", counter: "text-[#B5302A]", icon: "text-[#B5302A]" },
  // SG +2D / Office Email — navy blue family
  navy:    { bg: "bg-[#EAF0FB]", border: "border-[#1A4A8A33]", activeBorder: "border-[#1A4A8A]", counter: "text-[#1A4A8A]", icon: "text-[#1A4A8A]" },
  blue:    { bg: "bg-[#EAF0FB]", border: "border-[#1A4A8A33]", activeBorder: "border-[#1A4A8A]", counter: "text-[#1A4A8A]", icon: "text-[#1A4A8A]" },
  cyan:    { bg: "bg-[#EAF0FB]", border: "border-[#1A4A8A33]", activeBorder: "border-[#1A4A8A]", counter: "text-[#1A4A8A]", icon: "text-[#1A4A8A]" },
  indigo:  { bg: "bg-[#EAF0FB]", border: "border-[#1A4A8A33]", activeBorder: "border-[#1A4A8A]", counter: "text-[#1A4A8A]", icon: "text-[#1A4A8A]" },
  // External Calls — white card with neutral border, navy counter
  white:   { bg: "bg-white", border: "border-[#E2E8F0]", activeBorder: "border-[#1A4A8A]", counter: "text-[#1A4A8A]", icon: "text-[#1A4A8A]" },
  // Admin Review — orange family
  orange:  { bg: "bg-[#FEF3E8]", border: "border-[#C4600A33]", activeBorder: "border-[#C4600A]", counter: "text-[#C4600A]", icon: "text-[#C4600A]" },
  // Generic slate — fallback
  slate:   { bg: "bg-[#EDF0F4]", border: "border-[#CBD5E1]", activeBorder: "border-[#64748B]", counter: "text-[#334155]", icon: "text-[#64748B]" },
  // Legacy purple/violet kept as aliases of navy for backwards compat
  purple:  { bg: "bg-[#EAF0FB]", border: "border-[#1A4A8A33]", activeBorder: "border-[#1A4A8A]", counter: "text-[#1A4A8A]", icon: "text-[#1A4A8A]" },
  violet:  { bg: "bg-[#EAF0FB]", border: "border-[#1A4A8A33]", activeBorder: "border-[#1A4A8A]", counter: "text-[#1A4A8A]", icon: "text-[#1A4A8A]" },
};

// Common card classes — radius 14px + soft shadow per design tokens.
const cardBase =
  "rounded-[14px] border shadow-[0_1px_4px_rgba(0,0,0,0.06)] " +
  "cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200";

export const SummaryCard = ({ title, icon: Icon, color, total, items, onClick, active, testId }) => {
  const c = colorMap[color] || colorMap.navy;
  return (
    <div
      className={`${c.bg} ${cardBase} ${active ? `${c.activeBorder} shadow-md ring-2 ring-offset-1 ring-current/10` : c.border} p-4`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className={`h-4 w-4 ${c.icon}`} />}
        <h3 className="text-[9px] font-extrabold text-[#64748B] uppercase tracking-[0.06em]">{title}</h3>
      </div>
      <p className={`text-[32px] font-black ${total > 0 ? c.counter : "text-[#94A3B8]"} mb-2 leading-none font-mono-dm`}>{total}</p>
      {items && items.length > 0 && (
        <div className="space-y-1 mt-3">
          {items.map((item, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className={item.highlight ? "text-[#B5302A] font-semibold" : "text-[#475569]"}>{item.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full font-semibold ${item.highlight ? "bg-[#FCECEA] text-[#B5302A]" : "bg-[#F1F5F9] text-[#475569]"}`}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const StatCard = ({ title, titleShort, subtitle, icon: Icon, color, value, active, onClick, testId }) => {
  const c = colorMap[color] || colorMap.navy;
  return (
    <div
      className={`${c.bg} ${cardBase} ${active ? `${c.activeBorder} shadow-md ring-2 ring-offset-1 ring-current/10` : c.border} p-3 sm:p-4`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
        {Icon && <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 ${c.icon}`} />}
        {titleShort ? (
          <>
            <span className="sm:hidden text-[9px] font-extrabold text-[#64748B] uppercase tracking-[0.06em]">{titleShort}</span>
            <span className="hidden sm:inline text-[9px] font-extrabold text-[#64748B] uppercase tracking-[0.06em]">{title}</span>
          </>
        ) : (
          <span className="text-[9px] font-extrabold text-[#64748B] uppercase tracking-[0.06em] truncate">{title}</span>
        )}
      </div>
      <p className={`text-[28px] sm:text-[36px] font-black leading-none font-mono-dm ${value > 0 ? c.counter : "text-[#94A3B8]"}`}>{value}</p>
      {subtitle && <p className="hidden sm:block text-[10px] text-[#94A3B8] mt-2 uppercase tracking-[0.08em]">{subtitle}</p>}
    </div>
  );
};
